import { useUpdateSystems } from "@features/ax/assetRegistry/systems/hooks";
import { getCriticalityLabel } from "@features/ax/utils";
import { PageHeader } from "@shared/components/PageHeader";
import { Badge } from "@shared/ui/badge";
import { Button } from "@shared/ui/button";
import { Card, CardContent, CardHeader } from "@shared/ui/card";
import { Progress } from "@shared/ui/progress";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import { useState } from "react";
import { useParams } from "react-router-dom";
import CriticalityAssessmentModal from "../criticalityAssessmentModal"; // Adjust path as needed

export const RiskParameterTab: React.FC<{ data: any }> = ({ data }) => {
  const [selectedParameter, setSelectedParameter] = useState<string | null>(
    null
  );
  const { systemId } = useParams();

  const [showModal, setShowModal] = useState(false);
  const { mutateAsync: updateSystem, isPending: isUpdating } = useUpdateSystems(
    Number(systemId)
  );
  // ✅ Always make criticalityAssessment an array
  const riskParameters: any[] = Array.isArray(data?.criticalityAssessment)
    ? data.criticalityAssessment
    : [];

  const getScoreColor = (score: number) => {
    if (score >= 4) return "text-green-600";
    if (score >= 3) return "text-yellow-600";
    return "text-red-600";
  };

  // Convert existing parameters to the format expected by modal
  const existingScores = riskParameters.reduce(
    (acc: Record<string, number>, param: any) => {
      if (param.code && param.score !== undefined) {
        const fieldKey = param.code.split("#")[0];
        acc[fieldKey] = param.score;
      }
      return acc;
    },
    {}
  );

  const handleSave = async (assessmentData: any) => {
    const payload = {
      criticality_assessment: assessmentData.criticalityAssessment,
      remote_utility_id: getRemoteUtilityId(),
    };

    await updateSystem(payload);
    setShowModal(false);
  };
  const { label, color } = getCriticalityLabel(data?.criticalityScore);

  return (
    <div>
      <Card>
        <CardHeader>
          <div className="flex justify-between items-start">
            <PageHeader
              title="System Critical Assessment Parameters"
              subtitle="Detailed breakdown of Critical assessment factors for this system"
            />

            <Button onClick={() => setShowModal(true)}>Update Scores</Button>
          </div>
        </CardHeader>
        <CardContent className="mt-3">
          {riskParameters.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No risk parameters data available
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-center">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-gray-400"></div>
                  <span className="text-sm text-gray-600 mr-3">
                    Critical Score
                  </span>
                </div>
                <div className="text-right">
                  <span className={`font-semibold ${color}`}>
                    {data?.criticalityScore ?? "NA"}
                  </span>
                  <span className={`text-sm ml-2 ${color}`}>{label}</span>
                </div>
              </div>

              {riskParameters.map((param: any, index: number) => (
                <div
                  key={index}
                  className="border rounded-lg p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                  onClick={() =>
                    setSelectedParameter(
                      selectedParameter === param.name ? null : param.name
                    )
                  }
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <span className="font-medium">{param.name}</span>
                        <Badge variant="outline">{param.weight}% Weight</Badge>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <div
                          className={`text-lg font-semibold ${getScoreColor(
                            param.score
                          )}`}
                        >
                          {param.score}/5
                        </div>
                        <div className="text-xs text-gray-500">Score</div>
                      </div>
                      <Progress value={param.score * 20} className="w-20 h-2" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Criticality Assessment Modal */}
      <CriticalityAssessmentModal
        open={showModal}
        onClose={() => setShowModal(false)}
        isUpdating={isUpdating}
        existingScores={existingScores}
        onSave={handleSave}
        title="System Criticality Assessment"
        entityName={data?.name || "System"}
      />
    </div>
  );
};
